import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MapPin, Instagram } from "lucide-react";
import { toast } from "sonner";
import { api, setSeo, assetUrl } from "@/lib/api";
import { STORES, SITE } from "@/data/site";
import { useSite } from "@/context/SiteContext";
import ProductGrid, { SectionHeading } from "@/components/ProductGrid";
import Testimonials from "@/components/Testimonials";

const HERO = "https://images.unsplash.com/photo-1762605135376-ae5af70a5628?crop=entropy&cs=srgb&fm=jpg&q=90&w=1600";
const EDITORIAL = "https://images.unsplash.com/photo-1772474587292-08b3e8932acd?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200";
const STORY = "https://images.unsplash.com/photo-1762605135012-56a59a059e60?crop=entropy&cs=srgb&fm=jpg&q=90&w=1000";
const PARTY = "https://images.unsplash.com/photo-1668028563825-f3b7138db3de?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200";
const MALAY = "https://images.unsplash.com/photo-1613447895817-e617a4093f50?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200";
const HAJJ = "https://images.unsplash.com/photo-1771170983433-1576bc4a7eec?crop=entropy&cs=srgb&fm=jpg&q=90&w=1200";
const INSTA = [
  "https://images.unsplash.com/photo-1772474569781-2fb1c6539f8c?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1574297500578-afae55026ff3?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1726591383658-37fc19442523?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1558027309-0844844295f7?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1668028554553-f83cac89ce0f?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
  "https://images.unsplash.com/photo-1680789527271-f1fc1528f27e?crop=entropy&cs=srgb&fm=jpg&q=85&w=500",
];

export default function Home() {
  useEffect(() => setSeo("Adam's The Islamic Boutique | Premium Abaya & Hijab Shop Chennai & India", "Shop premium abayas, hijabs, jilbabs & modest fashion online in India. Adam's The Islamic Boutique — elegant Islamic clothing with stores in Chennai & Bengaluru."), []);
  const { categories, settings } = useSite();

  const { data: newArrivals = [], isLoading: l1 } = useQuery({ queryKey: ["home-new"], queryFn: async () => (await api.get("/products?flag=new")).data });
  const { data: bestSellers = [], isLoading: l2 } = useQuery({ queryKey: ["home-best"], queryFn: async () => (await api.get("/products?flag=bestseller")).data });

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[82vh] max-h-[820px] min-h-[560px] overflow-hidden" data-testid="hero-section">
        <img src={HERO} alt="Elegant abaya and hijab modest fashion" className="absolute inset-0 w-full h-full object-cover object-top kenburns" />
        <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 via-espresso/20 to-transparent" />
        <div className="absolute inset-0 flex items-end lg:items-center">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 w-full pb-16 lg:pb-0">
            <div className="max-w-xl fade-in-up">
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-ivory leading-[1.05] font-medium">Modesty,<br />Redefined.</h1>
              <p className="text-ivory/85 mt-5 text-base lg:text-lg font-sans max-w-md leading-relaxed">Discover timeless modest fashion designed with elegance, comfort and confidence.</p>
              <div className="flex flex-wrap gap-4 mt-8">
                <Link to="/category/abayas" className="bg-ivory text-espresso px-8 py-3.5 rounded-full text-sm font-medium tracking-wide hover:bg-champagne transition-colors" data-testid="hero-shop-abayas-button">SHOP ABAYAS</Link>
                <Link to="/shop" className="border border-ivory/60 text-ivory px-8 py-3.5 rounded-full text-sm font-medium tracking-wide hover:bg-ivory/10 transition-colors" data-testid="hero-explore-button">EXPLORE COLLECTION</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SHOP BY CATEGORY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16 lg:py-24" data-testid="category-section">
        <SectionHeading overline="Curated Collections" title="Shop by Category" subtitle="Explore our thoughtfully designed collections, crafted for the modern modest woman." />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 lg:gap-6">
          {categories.map((c) => (
            <Link key={c.key} to={`/category/${c.key}`} className="group relative overflow-hidden rounded-2xl aspect-[4/5] hover-zoom bg-beige" data-testid={`category-card-${c.key}`}>
              {c.image && <img src={assetUrl(c.image)} alt={c.name} className="w-full h-full object-cover" />}
              <div className="absolute inset-0 bg-gradient-to-t from-espresso/70 to-transparent" />
              <div className="absolute bottom-0 inset-x-0 p-5 lg:p-6">
                <h3 className="font-serif text-xl lg:text-2xl text-ivory">{c.name}</h3>
                <span className="inline-flex items-center gap-1.5 text-champagne-light text-xs mt-1 tracking-wide">Shop Now <ArrowRight className="w-3.5 h-3.5" /></span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 pb-16 lg:pb-24" data-testid="bestsellers-section">
        <SectionHeading overline="Loved by Many" title="Best Sellers" subtitle="Our most adored pieces, chosen again and again by the Adam's family." />
        <ProductGrid products={bestSellers.slice(0, 8)} loading={l2} />
      </section>

      {/* OUR STORY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-testid="story-section">
        <div>
          <p className="overline mb-4">Est. 1992</p>
          <h2 className="font-serif text-4xl lg:text-5xl text-espresso leading-tight">A Legacy of Modest Fashion Since 1992</h2>
          <p className="text-espresso/65 mt-6 leading-relaxed font-sans">For over three decades, Adam's The Boutique has been a trusted name in modest fashion, built on a simple philosophy — to bring together elegance, modesty, quality and contemporary style.</p>
          <p className="text-espresso/65 mt-4 leading-relaxed font-sans">What began as a humble venture in 1992 has grown into a well-loved boutique with a loyal community across generations — from beautifully crafted abayas, hijabs and jilbabs to accessories and Hajj &amp; Umrah essentials, each collection chosen for comfort, quality and effortless elegance.</p>
          <Link to="/about" className="inline-flex items-center gap-2 mt-8 text-espresso gold-underline text-sm" data-testid="story-about-link">Read Our Story <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <div className="rounded-3xl overflow-hidden aspect-[4/5] hover-zoom">
          <img src={STORY} alt="Adam's boutique story" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* STORE LOCATIONS */}
      <section className="bg-espresso py-16 lg:py-24" data-testid="stores-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-champagne-light overline mb-3">Visit Us</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ivory">Our Boutiques</h2>
            <p className="text-ivory/60 mt-4 font-sans">Experience the collection in person across Chennai & Bengaluru.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {STORES.map((s) => (
              <a key={s.name} href={s.maps} target="_blank" rel="noreferrer" className="bg-ivory/5 border border-ivory/10 rounded-2xl p-6 hover:border-champagne/40 hover:bg-ivory/10 transition-all group" data-testid={`store-card-${s.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <MapPin className="w-6 h-6 text-champagne mb-4" />
                <h3 className="font-serif text-xl text-ivory">{s.name}</h3>
                <p className="text-champagne-light text-xs uppercase tracking-wider mt-0.5">{s.city}</p>
                <p className="text-ivory/60 text-sm mt-3 leading-relaxed">{s.address}</p>
                <p className="text-ivory/50 text-xs mt-3">{s.hours}</p>
                {s.phone && <p className="text-champagne-light text-xs mt-1">{s.phone}</p>}
                <span className="inline-flex items-center gap-1.5 text-champagne text-xs mt-4 group-hover:gap-2.5 transition-all">Get Directions <ArrowRight className="w-3.5 h-3.5" /></span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-8 py-16 lg:py-24" data-testid="instagram-section">
        <div className="text-center mb-10">
          <p className="overline mb-3">@{settings.instagram}</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-espresso">Follow Our Journey</h2>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3">
          {INSTA.map((img, i) => (
            <a key={img} href={settings.instagram_url} target="_blank" rel="noreferrer" className="relative aspect-square overflow-hidden rounded-xl group">
              <img src={img} alt="Instagram post" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-espresso/0 group-hover:bg-espresso/40 flex items-center justify-center transition-colors">
                <Instagram className="w-6 h-6 text-ivory opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* NEWSLETTER */}
      <NewsletterBand />
    </div>
  );
}

function NewsletterBand() {
  const [email, setEmail] = React.useState("");
  const [done, setDone] = React.useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/newsletter", { email }); setDone(true); } catch { toast.error("Could not subscribe right now. Please try again."); }
  };
  return (
    <section className="bg-nude/40 py-16 lg:py-24" data-testid="newsletter-section">
      <div className="max-w-2xl mx-auto px-6 text-center">
        <p className="overline mb-3">Newsletter</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-espresso">Join the Adam's Family</h2>
        <p className="text-espresso/60 mt-4 font-sans">Be the first to discover new arrivals, exclusive collections and special offers.</p>
        {done ? (
          <p className="mt-8 text-champagne font-serif text-2xl">Thank you for joining us. 🤍</p>
        ) : (
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 mt-8 max-w-md mx-auto" data-testid="newsletter-form">
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Enter your email" className="flex-1 border border-espresso/20 rounded-full px-6 py-3.5 bg-ivory outline-none focus:border-champagne text-sm" data-testid="newsletter-input" />
            <button type="submit" className="bg-espresso text-ivory px-8 py-3.5 rounded-full text-sm font-medium hover:bg-espresso/90 transition-colors" data-testid="newsletter-submit">Subscribe</button>
          </form>
        )}
      </div>
    </section>
  );
}
